import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import {
  ArrowLeft,
  Copy,
  Check,
  Archive,
  AlertTriangle,
} from 'lucide-react';
import { adminApi, RatePlan, UpdateRatePlanRequest } from '../services/adminApi';
import './AdminDashboard.css';

// Map the status color names still returned by getStatusBadge onto shadcn Badge classes,
// preserving the status color SEMANTICS after the migration (mirrors OrgBillingTab):
// success = emerald, warning = amber, informative = neutral gray.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

function CopyableId({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <span className="copyable-id" title={value}>
      {label && <span className="copyable-id-label">{label}: </span>}
      <code>{value.length > 24 ? value.slice(0, 8) + '...' + value.slice(-8) : value}</code>
      <button className="copy-btn" onClick={handleCopy} title="Copy to clipboard">
        {copied ? <Check /> : <Copy />}
      </button>
    </span>
  );
}

function RatePlanDetailPage() {
  const { planId } = useParams<{ planId: string }>();
  const [plan, setPlan] = useState<RatePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit form
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<UpdateRatePlanRequest>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Archive confirmation
  const [showArchive, setShowArchive] = useState(false);

  const fetchPlan = useCallback(async () => {
    if (!planId) return;
    try {
      setLoading(true);
      const data = await adminApi.getRatePlan(planId);
      setPlan(data);
      setEditForm({
        name: data.name,
        description: data.description || '',
        basePrice: data.basePrice,
        includedTokens: data.includedTokens,
        overageRatePerThousand: data.overageRatePerThousand,
        stripePriceId: data.stripePriceId || '',
      });
      setError(null);
    } catch (err) {
      console.error('Failed to fetch rate plan:', err);
      setError('Failed to load rate plan');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const handleSave = async () => {
    if (!planId || !plan) return;

    setSaving(true);
    setSaveMessage(null);
    try {
      const updates: UpdateRatePlanRequest = {};
      if (editForm.name !== plan.name) updates.name = editForm.name;
      if (editForm.description !== (plan.description || '')) updates.description = editForm.description;
      if (editForm.basePrice !== plan.basePrice) updates.basePrice = editForm.basePrice;
      if (editForm.includedTokens !== plan.includedTokens) updates.includedTokens = editForm.includedTokens;
      if (editForm.overageRatePerThousand !== plan.overageRatePerThousand) updates.overageRatePerThousand = editForm.overageRatePerThousand;
      if (editForm.stripePriceId !== (plan.stripePriceId || '')) updates.stripePriceId = editForm.stripePriceId;

      await adminApi.updateRatePlan(planId, updates);
      setSaveMessage('Saved successfully');
      setEditing(false);
      fetchPlan();
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save:', err);
      setSaveMessage('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!planId) return;
    try {
      await adminApi.archiveRatePlan(planId);
      setShowArchive(false);
      fetchPlan();
    } catch (err) {
      console.error('Failed to archive:', err);
    }
  };

  const formatTokens = (tokens: number) =>
    tokens >= 1_000_000 ? (tokens / 1_000_000).toFixed(1) + 'M' : tokens.toLocaleString();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, 'success' | 'warning' | 'informative'> = {
      active: 'success', archived: 'informative', draft: 'warning',
    };
    return <Badge variant="outline" className={badgeClass(colors[status] || 'informative')}>{status}</Badge>;
  };

  const handleBack = () => { window.location.href = '/admin'; };


  if (loading) {
    return (
      <div className="org-detail-page">
        <div className="admin-loading"><Spinner size={32} label="Loading rate plan..." /></div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="org-detail-page">
        <Button variant="ghost" onClick={handleBack}><ArrowLeft />Back</Button>
        <div className="admin-error"><AlertTriangle /><p>{error || 'Not found'}</p></div>
      </div>
    );
  }

  // Some rate plans come back without a CoveredProductIds array (backend omits it),
  // so the camelCased response has no `coveredProductIds` field. Default to [] so the
  // page renders a "-" empty state instead of throwing on `.length`/`.join`.
  const coveredProductIds = plan.coveredProductIds ?? [];

  return (
    <div className="org-detail-page">
      <Button variant="ghost" onClick={handleBack} style={{ marginBottom: '1rem' }}>
        <ArrowLeft />
        Back to Rate Plans
      </Button>

      {/* Header Card */}
      <Card className="org-header-card">
        <div className="org-header-top">
          <div className="org-header-title">
            <h1>{plan.name}</h1>
            <div className="org-header-meta">
              {getStatusBadge(plan.status)}
              <CopyableId value={plan.id} />
            </div>
          </div>
          <div className="org-header-actions">
            {!editing && (
              <Button variant="ghost" onClick={() => setEditing(true)}>Edit</Button>
            )}
            {plan.status !== 'archived' && (
              <Button variant="ghost" onClick={() => setShowArchive(true)}>
                <Archive />
                Archive
              </Button>
            )}
          </div>
        </div>

        <div className="org-header-stats">
          <div className="stat-item">
            <span className="stat-label">Product</span>
            <span className="stat-value" style={{ fontSize: '0.9rem' }}>{plan.productId}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Base Price</span>
            <span className="stat-value">${plan.basePrice.toFixed(2)}/mo</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Included Tokens</span>
            <span className="stat-value">{formatTokens(plan.includedTokens)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Overage Rate</span>
            <span className="stat-value">${plan.overageRatePerThousand.toFixed(3)}/1K</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Assigned Orgs</span>
            <span className="stat-value">{plan.assignedOrganizationCount}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Billing</span>
            <span className="stat-value" style={{ fontSize: '0.9rem' }}>{plan.billingInterval}</span>
          </div>
        </div>
      </Card>

      {/* Plan Details / Edit Form */}
      {editing ? (
        <Card className="settings-form">
          <h3>Edit Rate Plan</h3>

          <div className="settings-field">
            <label>Name</label>
            <Input
              value={editForm.name || ''}
              onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
              style={{ width: '100%' }}
            />
          </div>

          <div className="settings-field">
            <label>Description</label>
            <Textarea
              value={editForm.description || ''}
              onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              style={{ width: '100%' }}
            />
          </div>

          <div className="settings-field">
            <label>Base Price (USD/month)</label>
            <Input
              type="number"
              value={String(editForm.basePrice ?? '')}
              onChange={(e) => setEditForm(f => ({ ...f, basePrice: parseFloat(e.target.value) || 0 }))}
              style={{ width: '200px' }}
            />
          </div>

          <div className="settings-field">
            <label>Included Tokens</label>
            <Input
              type="number"
              value={String(editForm.includedTokens ?? '')}
              onChange={(e) => setEditForm(f => ({ ...f, includedTokens: parseInt(e.target.value) || 0 }))}
              style={{ width: '200px' }}
            />
          </div>

          <div className="settings-field">
            <label>Overage Rate (per 1K tokens)</label>
            <Input
              type="number"
              value={String(editForm.overageRatePerThousand ?? '')}
              onChange={(e) => setEditForm(f => ({ ...f, overageRatePerThousand: parseFloat(e.target.value) || 0 }))}
              style={{ width: '200px' }}
            />
          </div>

          <div className="settings-field">
            <label>Stripe Price ID</label>
            <Input
              value={editForm.stripePriceId || ''}
              onChange={(e) => setEditForm(f => ({ ...f, stripePriceId: e.target.value }))}
              placeholder="price_..."
              style={{ width: '100%' }}
            />
          </div>

          <div className="settings-actions">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
            {saveMessage && (
              <p style={{ color: saveMessage.includes('Failed') ? '#d13438' : '#107c10' }}>
                {saveMessage}
              </p>
            )}
          </div>
        </Card>
      ) : (
        <div className="overview-tab">
          <Card className="info-section">
            <h3>Plan Configuration</h3>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Description</span>
                <span className="info-value">{plan.description || '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Currency</span>
                <span className="info-value">{plan.currency}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Stripe Price ID</span>
                <span className="info-value">
                  {plan.stripePriceId ? <CopyableId value={plan.stripePriceId} /> : '-'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Covered Products</span>
                <span className="info-value">
                  {coveredProductIds.length > 0 ? coveredProductIds.join(', ') : '-'}
                </span>
              </div>
            </div>
          </Card>

          <Card className="info-section">
            <h3>History</h3>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Created</span>
                <span className="info-value">{formatDate(plan.createdAt)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Last Updated</span>
                <span className="info-value">{formatDate(plan.updatedAt)}</span>
              </div>
            </div>
          </Card>

          {saveMessage && (
            <p style={{ color: '#107c10', marginTop: '0.5rem' }}>{saveMessage}</p>
          )}
        </div>
      )}

      {/* Archive Confirmation */}
      {showArchive && (
        <div className="suspend-overlay">
          <Card className="suspend-card">
            <h3>Archive Rate Plan</h3>
            <p>
              Are you sure you want to archive <b>{plan.name}</b>?
              This plan has {plan.assignedOrganizationCount} assigned organization(s).
              Archived plans cannot be assigned to new organizations.
            </p>
            <div className="suspend-actions">
              <Button variant="outline" onClick={() => setShowArchive(false)}>Cancel</Button>
              <Button onClick={handleArchive}>Archive</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default RatePlanDetailPage;
